import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { GoogleMap } from '@react-google-maps/api'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarker'
import { fitMarkersOnMap } from 'lib/maps/googleMapUtils'
import Group from 'components/utils/Group'

interface Props {
  title: string;
  pickupTitle?: string;
  primaryMapMarker?: App.MapMarker;
  pickupMapMarker?: App.MapMarker;
  locationMapMarkerList?: Array<App.MapMarker>;
  googleMapId: string;
  zoomLevel: number;
  style: React.CSSProperties;
  onZoomChanged?: (zoomIn: boolean) => void;
}

function ExperienceMap(props: Props) {
  const {
    primaryMapMarker,
    title,
    pickupTitle,
    pickupMapMarker,
    locationMapMarkerList,
    googleMapId,
    zoomLevel,
    style,
    onZoomChanged,
  } = props
  const [map, setMap] = useState<google.maps.Map>()

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => { setMap(map) }, [])

  const locationMarkers = useMemo(() => {
    const list = locationMapMarkerList ?? []
    return list.map((marker: App.MapMarker) => {
      return <PropertyMarker marker={marker} title={marker.name} key={marker.id} multipleMarkers />
    })
  }, [locationMapMarkerList])

  useEffect(() => {
    if (map) {
      const allMarkers = [
        primaryMapMarker,
        pickupMapMarker,
        ...(locationMapMarkerList ?? []),
      ].filter(Boolean)
      fitMarkersOnMap(map, allMarkers)
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps
  const prevZoomLevel = useRef(zoomLevel)
  return <Group direction="vertical">
    <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
      <GoogleMap
        id={googleMapId}
        zoom={zoomLevel}
        onLoad={onLoad}
        mapContainerStyle={style}
        onZoomChanged={() => {
          if (map) {
            const current = prevZoomLevel.current
            prevZoomLevel.current = map.getZoom()
            onZoomChanged?.(prevZoomLevel.current > current)
          } }}
      >
        {primaryMapMarker && <PropertyMarker
          marker={primaryMapMarker}
          title={`Experience: ${title}`}
          multipleMarkers
        />}
        {locationMarkers.map((marker) => marker)}
        {pickupMapMarker && <PropertyMarker
          marker={pickupMapMarker}
          title={`Pickup point: ${pickupTitle}`}
          multipleMarkers
        />}
      </GoogleMap>
    </EnsureGoogleMapsLoaded>
  </Group>
}

export default ExperienceMap
